import moment from "moment";
import type { CheckoutFormValues } from "../types/landing.types";
import type { CheckoutSessionState } from "../utils/checkoutSession";
import type {
  CheckoutPayload,
  CheckoutResponseData,
  StudentByEmailApi,
} from "./landingCheckout.types";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/** Normalize form DOB (YYYY-MM-DD from DatePicker) to API-friendly DD-MM-YYYY. */
function toCheckoutDob(dob: string): string {
  const parsed = moment(dob.trim(), ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"], true);
  if (!parsed.isValid()) {
    throw new Error("Date of birth is invalid.");
  }
  return parsed.format("DD-MM-YYYY");
}

/** Map by-email API DOB (typically DD-MM-YYYY) to DatePicker YYYY-MM-DD. */
export function toCheckoutFormDob(dob: string): string {
  const parsed = moment(
    dob.trim(),
    ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY"],
    true
  );
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

function toCheckoutFormGender(
  gender: string | undefined
): CheckoutFormValues["gender"] | "" {
  const normalized = gender?.trim().toLowerCase();
  if (normalized === "male" || normalized === "female" || normalized === "other") {
    return normalized;
  }
  return "";
}

/** Map by-email response into checkout form fields (excludes email). */
export function toCheckoutFormValuesFromStudent(student: StudentByEmailApi): {
  studentName: string;
  phone: string;
  dob: string;
  gender?: CheckoutFormValues["gender"];
  /** Only lock fields that the API actually returned with a usable value */
  lockedFields: {
    studentName: boolean;
    phone: boolean;
    dob: boolean;
    gender: boolean;
  };
} | null {
  const studentName = student.name?.trim() ?? "";
  const phone = student.phone_number?.trim() ?? "";
  const dob = student.dob?.trim() ? toCheckoutFormDob(student.dob) : "";
  const gender = toCheckoutFormGender(student.gender);

  // Treat as not found only when the payload has nothing usable to show
  if (!studentName && !phone && !dob && !gender) {
    return null;
  }

  return {
    studentName,
    phone,
    dob,
    ...(gender ? { gender } : {}),
    lockedFields: {
      studentName: Boolean(studentName),
      phone: Boolean(phone),
      dob: Boolean(dob),
      gender: Boolean(gender),
    },
  };
}

export function toCheckoutPayload(
  values: CheckoutFormValues,
  session: CheckoutSessionState
): CheckoutPayload {
  const schoolId = session.course.schoolId?.trim() ?? "";
  const classId = session.classId?.trim() ?? "";
  const validityDays = session.course.durationDays;
  const gender = values.gender;

  if (!schoolId || !isValidUuid(schoolId)) {
    throw new Error("School is not available for this class.");
  }
  if (!classId || !isValidUuid(classId)) {
    throw new Error("Class is not available for checkout.");
  }
  if (!validityDays || validityDays <= 0) {
    throw new Error("Class validity is not available for checkout.");
  }
  if (gender !== "male" && gender !== "female" && gender !== "other") {
    throw new Error("Gender is required.");
  }

  return {
    student_name: values.studentName.trim(),
    email: values.email.trim(),
    phone_number: values.phone.trim(),
    validity_in_days: validityDays,
    school_id: schoolId,
    class_id: classId,
    dob: toCheckoutDob(values.dob),
    gender,
  };
}

/** Checkout succeeded when payment is already completed (no gateway redirect needed). */
export function isCheckoutPaymentComplete(data: CheckoutResponseData): boolean {
  return data.payment_status?.toUpperCase() === "COMPLETED";
}

/** Verify-payment succeeded when backend marks purchase COMPLETED. */
export function isVerifyPaymentComplete(
  paymentStatus: string | undefined
): boolean {
  return paymentStatus?.toUpperCase() === "COMPLETED";
}
